/* eslint-disable react-hooks/exhaustive-deps */
import { v4 } from "uuid";
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactGA from "react-ga4";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ERR_NETWORK, chatInteractionAPI } from "../../api/llm/chatInteractionAPI";
import { getItineraryImageURL } from "../../api/llm/getItineraryImageURL";
import { planTripDetails } from "../../api/llm/planTripDetails";
import { cloneSharedTravelPlanIfNeeded } from "../../api/itinerary/cloneSharedTravelPlanIfNeeded";
import UserFeedbackDialog from "../../component/home&Landingpage/mainPageApp/dialog/userFeedbackDialog/UserFeedbackDialog";
import SwipeableEdgeDrawer from "../../component/reusableComponent/swipeableEdgeDrawer/SwipeableEdgeDrawer";
import { API_FAILURE_MESSAGE, ROUTES } from "../../constant";
import {
  DialogSelected,
  ENVIRONMENT_TYPE,
  INVALID_JSON_PROMPT,
  LLM_RESPONSE_ERROR,
  PROMPT,
  SOCKET_CONNECTION_TYPE,
} from "../../constant/common";
import { DESTINATION_LIST_DATA_GRID, SELECTED_CHAT_DATA } from "../../constant/dummyData";
import { STREAMING_ENABLE, environment, ENABLE_GA_CUSTOM_EVENTS } from "../../constant/environment";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { Messages } from "../../constant/messages/toastMessages";
import { ScreenTypes } from "../../enums";
import { toastMessage } from "../../helpers/toast/toastMessage";
import { iChatStreamingPayload } from "../../interface/common";
import { iChatData, iCityDetailsResult } from "../../types";
import { checkIfThereIsDestination, getItineraryVersion, getTripName, scrollToBottom } from "../../utils";
import { appendErrorMessageTochat } from "../../utils/appendErrorMessageToChat";
import { consoleLog } from "../../utils/consoleLog/consoleLog";
import { filterErrorMessages } from "../../utils/itineraryUtils/filterErrorchatFromPromptContext";
import { checkIfRateLimitExceed, updateRateLimitCount } from "../../utils/rateLimit/rateLimit";
import { useDataStreamingContext } from "../dataStreamingContext/DataStreamingContext";
import { iDataStreamingContext } from "../dataStreamingContext/dataStreamingContextInterface";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../globalContext/interface";
import { useHistoryContext } from "../historyContext/historyContext";
import { iHistoryContext } from "../historyContext/interface";
import { iSocketContext, useSocketContext } from "../socketContext/SocketContext";
import { iTopDestinationContext } from "./topDestinationContextInterface";
import { getChatId } from "../../utils/chat/chatId";
import { useNavigationUrl } from "../../hooks/useNavigationUrl";
import { useTheresiasEventBridge } from "../../services/intent/useTheresiasEventBridge";
import { isNonEmptyMessageContent } from "../../streaming/finalizeGuards";

// Strip UI-only fields and the recursively-nested `results` payload from
// activeChatData before shipping it to the LLM as `currentItineray`. The
// `results` recursion comes from the share-viewer flow: ViewShareItinerary
// stamps `results: scd` on the synthesized bubble (so back-button gates
// pass), the clone save persists that, and each subsequent load adds
// another nested `results: scd` wrap — multiplying the JSON 4-5× over a
// few round-trips. The LLM only needs the top-level itinerary fields
// (destinationList with tripDetails, descriptions, dates, preferences),
// so we drop the bloat entirely.
const ITINERARY_LLM_OMIT_FIELDS = new Set([
  // recursive bloat
  "results",
  "result",
  // chat bubble UI fields
  "prompt",
  "message",
  "preText",
  "postText",
  "isLoading",
  "isUserMessage",
  "isCompleteMessage",
  "isErrorMessage",
  "singleSelectedDestination",
  "isProcessInterrupted",
  // frontend-only state / share-flow plumbing
  "travelPlanId",
  "sharedSourceTravelPlanId",
  "isSaved",
  "isShared",
  "checkFlag",
  "checkedFlag",
  "attachedToChatId",
]);

export function sanitizeItineraryForLLM(scd: any): any {
  if (!scd || typeof scd !== "object") return scd;
  const out: Record<string, any> = {};
  for (const key of Object.keys(scd)) {
    if (ITINERARY_LLM_OMIT_FIELDS.has(key)) continue;
    out[key] = scd[key];
  }
  return out;
}

// Build the conversation history sent to the LLM from visible chat bubbles.
// Destinations bubbles store their content in `destinationList` and have an
// empty `message` — they were silently dropped by an earlier filter that
// gated on message presence, breaking multi-turn flows that involved a card
// pick (LLM never saw the cards turn AND the user's pick had no chat record).
// This helper:
//   1. Includes destinations bubbles by synthesizing a one-line summary so
//      the LLM knows what cities were offered.
//   2. Surfaces the synthetic shared-trip greeting bubble's preText (the
//      "Hey {name}, this is your itinerary to {city} for {N} days…"
//      template) when message is empty — without this the LLM sees a bare
//      "Showed destination cards: ." line with no real context.
//   3. Falls back to city_name for destinationList entries (cloned shared
//      trips use city_name, not destination_name/destination_city).
//   4. Keeps regular text bubbles unchanged.
//   5. Drops only the loading placeholder.
const buildPromptContext = (chatData: any[]): any[] =>
  chatData
    .filter((item) => !item?.isLoading)
    .filter(
      (item) =>
        (item?.message && String(item.message).trim()) ||
        (item?.preText && String(item.preText).trim()) ||
        (item?.destinationList && item.destinationList.length > 0)
    )
    .map((item) => {
      // Bot bubble with no message — synthesize content from preText and/or
      // destinationList so the LLM has a meaningful turn instead of a bare
      // "Showed destination cards" placeholder.
      if (
        !item?.isUserMessage &&
        (!item?.message || !String(item.message).trim())
      ) {
        const greeting =
          item?.preText && String(item.preText).trim() ? String(item.preText).trim() : "";
        const cities =
          item?.destinationList && item.destinationList.length > 0
            ? item.destinationList
                .map(
                  (d: any) => d?.destination_name || d?.destination_city || d?.city_name
                )
                .filter(Boolean)
                .join(", ")
            : "";
        const cityLine = cities ? `Showed destination cards: ${cities}.` : "";
        const content = [greeting, cityLine].filter(Boolean).join(" ");
        return { role: "assistant", content };
      }
      return {
        role: item.isUserMessage ? "user" : "assistant",
        content: item.message,
        ...(item.isErrorMessage ? { isErrorMessage: true } : {}),
      };
    })
    .filter((entry) => isNonEmptyMessageContent(entry?.content));


let abortPlanTripDetails = new AbortController();

const TopDestinationContext = createContext({} as iTopDestinationContext);

export const TopDestinationContextProvider = ({ children }: { children: ReactNode }) => {
  // Context
  const state = useContext(StateContext);
  const {
    chatInputText,
    setChatInputText,
    chatData,
    setChatData,
    selectedChatData,
    setSelectedChatData,
    setIsItinerarySavedFlag,
    setSharedItineraryId,
    isMobile,
    setScreen,
    setPrevRoute,
    userInfo,
    socketId,
    openLoaderOverlay,
    setOpenLoaderOverlay,
    appConfig,
    setSelectedCityListPrompContext,
    accessJWT,
    abortControllerRef,
    setLoginCreateAccountState,
    setSignInClick,
    setFirstScreen,
    desktopScrollContainerRef,
    userLocation,
  }: iGlobalContext = state;
  const { setCallSaveHistoryAPIFlag }: iHistoryContext = useHistoryContext();

  const {
    streamingFlagAndResponseType,
    setStreamingFlagAndResponseType,
    handleOpenSocketConnection,
    handleCloseSocketConnection,
    connectionStatus,
    lastChatPayloadRef,
  }: iSocketContext = useSocketContext();

  const {
    disableInput,
    setDisableInput,
    chatAbortController,
    chatAbortControllerRef,
    startSocketStreamingTimer,
    // setStreamingInProgressFlag,
    setRegeneratingItinerary,
    outboundV2RequestRef,
  }: iDataStreamingContext = useDataStreamingContext();

  const navigate = useNavigate();
  const { navigateWithParams } = useNavigationUrl();
  const fireTheresiasEvent = useTheresiasEventBridge();
  const [showChatLoaderFlag, setShowChatLoaderFlag] = useState(false);

  /**
   * Opening Trip details for Single or Multi city
   * */
  async function handleViewSingleTripDetails(chatDataArgs: iChatData, navigateFlag: boolean) {
    setPrevRoute(ROUTES.TopDestination);
    setIsItinerarySavedFlag(false);
    setSharedItineraryId("");
    const tempChatData: iChatData = {
      ...chatDataArgs,
      destinationList: chatDataArgs.destinationList.map((ele: iCityDetailsResult) => ({
        ...ele,
        checkedFlag: isMobile ? true : !ele.checkedFlag,
      })),
    };
    setChatData((prev: iChatData[]) =>
      prev.map((ele: iChatData) => {
        if (ele.id === chatDataArgs.id) return tempChatData;
        else
          return {
            ...ele,
            destinationList: ele.destinationList.map((destListEle: iCityDetailsResult) => ({
              ...destListEle,
              checkedFlag: false,
            })),
          };
      })
    );
    const tempTripName = getTripName(chatDataArgs);
    setSelectedChatData({
      ...tempChatData,
      tripName: tempTripName,
    });
    if (!navigateFlag) return;
    if (isMobile) {
      const y =
        document.body.scrollTop ||
        document.documentElement.scrollTop ||
        window.scrollY ||
        0;
      sessionStorage.setItem(
        "_zenvoya_top_dest_scroll",
        JSON.stringify({ y, ts: Date.now() })
      );
      navigateWithParams(ROUTES.ItineraryDetails);
    } else {
      setScreen(ScreenTypes.SingleItinerary);
    }
  }

  async function handleShowMoreStreaming(chatDataArgs: iChatData) {
    toast.dismiss();
    const excludeDestinations = (chatDataArgs?.destinationList || [])
      .filter((d: any) => d?.destination_name)
      .map((d: any) => ({
        name: d?.destination_name || d?.city_name || d?.destination_city || "",
        country: d?.destination_country || d?.country || "",
      }));
    setTimeout(() => {
      setDisableInput(true);
      setStreamingFlagAndResponseType((prev: any) => ({
        ...prev,
        showMoreClickFlag: true,
      }));
    }, 200);
    setChatData((prev: iChatData[]) =>
      prev.map((chatDataEle: iChatData) => {
        if (chatDataEle.id === chatDataArgs.id) {
          const tempDestinationList: any = chatDataEle.destinationList;
          tempDestinationList.push(DESTINATION_LIST_DATA_GRID);
          tempDestinationList.push(DESTINATION_LIST_DATA_GRID);
          return {
            ...chatDataEle,
            isLoading: true,
            destinationList: tempDestinationList,
          };
        } else return chatDataEle;
      })
    );

    const payload: iChatStreamingPayload = {
      userId: userInfo?.id,
      chatId: getChatId(),
      userInput: null,
      messages: chatDataArgs.prompt,
      socketId: socketId ?? "",
      iteneryInput: null,
      showMore: true,
      excludeDestinations,
      userLocation,
    };
    handleOpenSocketConnection(payload);
    setRegeneratingItinerary(false);
    startSocketStreamingTimer();
  }
  async function handleShowMore(chatDataArgs: iChatData) {
    setTimeout(() => {
      setDisableInput(true);
    }, 200);

    setChatData((prev: iChatData[]) =>
      prev.map((chatDataEle: iChatData) => {
        if (chatDataEle.id === chatDataArgs.id)
          return {
            ...chatDataEle,
            isLoading: true,
          };
        else return chatDataEle;
      })
    );
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    let response: any = await chatInteractionAPI(
      accessJWT,
      userInfo?.id || "",
      // showMorePrompt?.length > 0 ? showMorePrompt[0]?.value :
      PROMPT.SHOW_MORE,
      chatDataArgs.prompt,
      abortController.signal,
      null,
      appConfig
    );
    if (!response && !abortPlanTripDetails.signal) {
      toastMessage.info(Messages["api.break.down"].messageTitle, Messages["api.break.down"].message);
      setDisableInput(false);
      setShowChatLoaderFlag(false);
      return;
    }
    response = checkIfThereIsDestination(response);
    if (response === LLM_RESPONSE_ERROR.jsonError.error) {
      setDisableInput(false);
      setShowChatLoaderFlag(false);
      if (environment !== ENVIRONMENT_TYPE.PRODUCTION) {
        toastMessage.error(
          Messages["SaveItinerary.destinationError"].messageTitle,
          Messages["SaveItinerary.destinationError"].message
        );
      }
      return;
    }
    setDisableInput(false);

    let citiesList = [];
    if (response?.data?.cities) {
      citiesList = response?.data?.cities;
    }
    const tempResponse = citiesList.map((ele: any) => ({
      ...ele,
      id: v4(),
      checkedFlag: false,
      locations: [],
    }));
    setChatData((prev: iChatData[]) =>
      prev.map((chatDataEle: iChatData) => {
        if (chatDataEle.id === chatDataArgs.id)
          return {
            ...chatDataEle,
            fromCity: response?.data?.from,
            noOfDays: response?.data?.numberOfdays,
            message: "",
            preText: response?.data?.preText,
            postText: response?.data?.postText,
            prompt: response?.data?.messages,
            isUserMessage: false,
            isLoading: false,
            singleSelectedDestination: false,
            destinationList: [...chatDataEle.destinationList, ...tempResponse],
          };
        else return chatDataEle;
      })
    );
    const currentItineraryList = tempResponse;
    for (let i = 0; i < currentItineraryList.length; i++) {
      const cityName = currentItineraryList[i].destination_city;
      let imageRespone = await getItineraryImageURL(accessJWT, cityName);
      let count = 0;
      while (!imageRespone || !imageRespone?.data?.image) {
        // Limiting the number of API call to 10 times incase of 409 error
        count++;
        if (count === 3) break;
        // re calling the API
        imageRespone = await getItineraryImageURL(accessJWT, cityName);
      }
      if (imageRespone?.statusCode === 200) {
        currentItineraryList[i].imageURL = imageRespone.data.image;
      } else {
        currentItineraryList[i].imageURL = "https://error.error";
      }
    }
    setDisableInput(false);
    setShowChatLoaderFlag(false);
  }

  /**
   * Handling selecting or unselecting
   * card from grid
   */
  const [selectedCheckboxDestinationId, setSelectedCheckboxDestinationId] = useState({
    cityDetailsId: "",
    destinationArgsId: "",
  });
  const lastTapCardIdRef = useRef<string>("");
  async function handleCheckboxChangeStreaming(cityDetails: iCityDetailsResult | any, destinationArgs: iChatData) {
    if (streamingFlagAndResponseType?.isStillStreamingFlag) return;

    if (cityDetails?.id && cityDetails.id === lastTapCardIdRef.current && (selectedChatData?.destinationList?.length ?? 0) > 0) {
      if (isMobile) {
        navigateWithParams(ROUTES.ItineraryDetails);
      } else {
        setScreen(ScreenTypes.ItineraryInfo);
        setFirstScreen(ScreenTypes.topDestination);
      }
      return;
    }

    consoleLog(["GA EVENTS DFDF", cityDetails, destinationArgs]);
    triggerGAEvent(GA_LABEL_ENUMS.chat_places_to_visit_card_click, "", "", "", {
      city: cityDetails?.destination_city,
      country: cityDetails?.destination_country,
      duration: cityDetails?.number_of_travel_days ?? "",
    });
    fireTheresiasEvent("home-place-suggestion-card-click", {
      city: cityDetails?.destination_city ?? "",
      country: cityDetails?.destination_country ?? "",
      duration: cityDetails?.number_of_travel_days ?? "",
    });
    setScreen("");
    setStreamingFlagAndResponseType((prev: any) => ({
      ...prev,
      chatId: destinationArgs?.id ?? "",
    }));
    toast.dismiss();
    setPrevRoute(ROUTES.TopDestination);
    setSelectedCheckboxDestinationId({
      cityDetailsId: cityDetails?.id,
      destinationArgsId: destinationArgs?.id,
    });
    let tempDestinationList: iCityDetailsResult[] =
      chatData.find((ele: iChatData) => ele.id === destinationArgs.id)?.destinationList || ([] as iCityDetailsResult[]);

    setIsItinerarySavedFlag(false);
    setSharedItineraryId("");
    setShowChatLoaderFlag(false);
    tempDestinationList = tempDestinationList.map((ele: iCityDetailsResult) => {
      if (cityDetails.id === ele.id) return { ...ele, checkedFlag: !ele.checkedFlag };
      return { ...ele, checkedFlag: false };
    });
    setChatData((prev: iChatData[]) =>
      prev.map((ele: iChatData) => {
        if (ele.id === destinationArgs.id)
          return {
            ...ele,
            destinationList: tempDestinationList,
          };
        else
          return {
            ...ele,
            destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
              ...chatDataEle,
              checkedFlag: false,
            })),
          };
      })
    );
    if (tempDestinationList.some((ele: iCityDetailsResult) => ele.checkedFlag)) {
      setSelectedChatData({ ...SELECTED_CHAT_DATA });
      setShowChatLoaderFlag(true);

      const tempSelectedChatData = {
        ...destinationArgs,
        destinationList: tempDestinationList,
      };
      const cityList = tempSelectedChatData.destinationList
        .filter((city) => city.checkedFlag)
        .map((city) => city.destination_city);
      setDisableInput(true);
      abortPlanTripDetails.abort();
      abortPlanTripDetails = new AbortController();
      const iteneryInput = {
        user_given_number_of_days: [
          {
            city_name: cityList[0],
            num_days: null,
          },
        ],
      };

      /** START if there is destination trip details already */
      if (cityDetails?.destinationList?.length > 0 && cityDetails?.destinationList?.[0]?.imageURL !== "") {
        setDisableInput(false);
        setSelectedChatData(cityDetails);
        /**
         * remove the selected checkbox
         */
        tempDestinationList = destinationArgs?.destinationList?.map((ele: iCityDetailsResult) => {
          return { ...ele, checkedFlag: false };
        });
        setChatData((prev: iChatData[]) =>
          prev.map((ele: iChatData) => {
            if (ele.id === destinationArgs.id)
              return {
                ...ele,
                destinationList: tempDestinationList,
              };
            else
              return {
                ...ele,
                destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
                  ...chatDataEle,
                  checkedFlag: false,
                })),
              };
          })
        );
        if (isMobile) {
          navigateWithParams(ROUTES.ItineraryDetails);
        } else {
          setScreen("singleItinerary");
        }
        return;
      }
      /** END */
      chatAbortControllerRef.current = chatAbortController;
      lastTapCardIdRef.current = cityDetails?.id ?? "";
      setSelectedChatData({ ...SELECTED_CHAT_DATA, destinationList: [], isItineraryStreaming: true });
      const messagesForRequest = filterErrorMessages(buildPromptContext(chatData));
      const pendingEntryId = v4();
      setChatData((prev: iChatData[]) => [
        ...prev,
        {
          id: pendingEntryId,
          message: "",
          prompt: [],
          isUserMessage: false,
          isLoading: true,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);
      scrollToBottom(desktopScrollContainerRef);
      outboundV2RequestRef.current = {
        messages: messagesForRequest,
        reqId: v4(),
        traceId: pendingEntryId.slice(0, 8),
        pendingEntryId,
        caller: "cardTap",
      };
      const payload: iChatStreamingPayload = {
        userId: userInfo?.id,
        chatId: getChatId(),
        userInput: null,
        messages: messagesForRequest,
        socketId: socketId || "",
        iteneryInput: iteneryInput,
        showMore: false,
        userLocation,
      };
      handleOpenSocketConnection(payload);
      startSocketStreamingTimer();
      setRegeneratingItinerary(false);
    }
  }

  async function handleCheckboxChange(cityDetails: any, destinationArgs: iChatData) {
    setPrevRoute(ROUTES.TopDestination);
    setSelectedCheckboxDestinationId({
      cityDetailsId: cityDetails?.id,
      destinationArgsId: destinationArgs?.id,
    });
    let tempDestinationList: iCityDetailsResult[] =
      chatData.find((ele: iChatData) => ele.id === destinationArgs.id)?.destinationList || ([] as iCityDetailsResult[]);

    setIsItinerarySavedFlag(false);
    setSharedItineraryId("");
    setShowChatLoaderFlag(false);
    tempDestinationList = tempDestinationList.map((ele: iCityDetailsResult) => {
      if (cityDetails.id === ele.id) return { ...ele, checkedFlag: !ele.checkedFlag };
      return { ...ele, checkedFlag: false };
    });
    setChatData((prev: iChatData[]) =>
      prev.map((ele: iChatData) => {
        if (ele.id === destinationArgs.id)
          return {
            ...ele,
            destinationList: tempDestinationList,
          };
        else
          return {
            ...ele,
            destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
              ...chatDataEle,
              checkedFlag: false,
            })),
          };
      })
    );
    if (tempDestinationList.some((ele: iCityDetailsResult) => ele.checkedFlag)) {
      setSelectedChatData({} as iChatData);
      setShowChatLoaderFlag(true);
      const selectedChatData = {
        ...destinationArgs,
        destinationList: tempDestinationList,
      };
      const cityList = selectedChatData.destinationList
        .filter((city) => city.checkedFlag)
        .map((city) => city.destination_city);
      setDisableInput(true);
      abortPlanTripDetails.abort();
      abortPlanTripDetails = new AbortController();
      const iteneryInput = {
        user_given_number_of_days: [
          {
            city_name: cityList[0],
            num_days: null,
          },
        ],
      };

      if (cityDetails?.destinationList?.length > 0) {
        setDisableInput(false);
        setSelectedChatData(cityDetails);
        /**
         * remove the selected checkbox
         */
        tempDestinationList = destinationArgs?.destinationList?.map((ele: iCityDetailsResult) => {
          return { ...ele, checkedFlag: false };
        });
        setChatData((prev: iChatData[]) =>
          prev.map((ele: iChatData) => {
            if (ele.id === destinationArgs.id)
              return {
                ...ele,
                destinationList: tempDestinationList,
              };
            else
              return {
                ...ele,
                destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
                  ...chatDataEle,
                  checkedFlag: false,
                })),
              };
          })
        );

        if (isMobile) {
          navigateWithParams(ROUTES.ItineraryDetails);
        } else {
          setScreen("singleItinerary");
        }
        return;
      }
      setOpenLoaderOverlay(true);
      let response = await planTripDetails(accessJWT, abortPlanTripDetails, selectedChatData.prompt, iteneryInput);
      setDisableInput(false);
      response = checkIfThereIsDestination(response);
      // response = undefined
      if (response === LLM_RESPONSE_ERROR.jsonError.error) {
        setShowChatLoaderFlag(false);
        setOpenLoaderOverlay(false);
        setChatData((prev: iChatData[]) =>
          prev.map((ele: iChatData) => {
            return {
              ...ele,
              destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
                ...chatDataEle,
                checkedFlag: false,
              })),
            };
          })
        );
        if (environment !== ENVIRONMENT_TYPE.PRODUCTION) {
          toastMessage.error(
            Messages["SaveItinerary.destinationError"].messageTitle,
            Messages["SaveItinerary.destinationError"].message
          );
        }
        return;
      }
      setSelectedCityListPrompContext(response?.data?.messages[response?.data?.messages?.length - 1] || {});

      if (response?.statusCode !== 200 || !response) {
        setOpenLoaderOverlay(false);
        if (!abortPlanTripDetails?.signal?.aborted)
          toastMessage.error(Messages["api.break.down"].messageTitle, Messages["api.break.down"].message);
        console.error(API_FAILURE_MESSAGE);
        return;
      }
      if (!response?.data?.cities || response?.data?.cities?.length === 0) {
        setChatData((prev: any) => [
          ...prev,
          {
            ...response?.data,
            id: v4(),
            message: response?.data?.result,
            isUserMessage: false,
            prompt: [],
            isLoading: false,
            destinationList: [],
            singleSelectedDestination: false,
          },
        ]);
        setOpenLoaderOverlay(false);
        setCallSaveHistoryAPIFlag({
          flag: true,
          method: "PUT",
        });
        return;
      }
      const tempDestinationList_2 = selectedChatData.destinationList.map(
        (destinationListEle: iCityDetailsResult, index: number) => {
          if (destinationListEle.checkedFlag) {
            const tempObj = {
              ...destinationListEle,
              results: response?.data?.results,
              ...response?.data,
              cities: [],
              city_wise_itinerary: [],
              result: "",
              messages: [],
              fromCity: response?.data?.from,
              noOfDays: response?.data?.numberOfdays,
              id: v4(),
              message: "",
              preText: response?.data?.preText,
              postText: response?.data?.postText,
              prompt: response?.data?.messages,
              isUserMessage: false,
              isLoading: false,
              singleSelectedDestination: response?.data?.singleCity,
              destinationList: response?.data?.cities.map((ele: any) => ({
                ...ele,
                id: v4(),
                checkedFlag: true,
                locations: [],
                imageURL: destinationListEle?.imageURL,
                destination_name: destinationListEle?.destination_name,
              })),
              itinerary_version: 1,
            };
            setSelectedChatData(tempObj);
            return tempObj;
          }
          return destinationListEle;
        }
      );
      const tempTripName = tempDestinationList_2.reduce((acc: string, ele: iCityDetailsResult, index: number) => {
        if (ele.checkedFlag) {
          if (index === selectedChatData?.destinationList.length - 1) return acc + ele.destination_city;
          else return acc + ele.destination_city;
        }
        return acc;
      }, "");
      setSelectedChatData((prev: any) => ({
        ...prev,
        tripName: tempTripName,
        iteneryInput: iteneryInput,
      }));
      setShowChatLoaderFlag(false);

      /**
       * remove the selected checkbox
       */
      tempDestinationList = tempDestinationList_2.map((ele: iCityDetailsResult) => {
        return { ...ele, checkedFlag: false };
      });
      setChatData((prev: iChatData[]) =>
        prev.map((ele: iChatData) => {
          if (ele.id === destinationArgs.id)
            return {
              ...ele,
              // ...response?.data,
              destinationList: tempDestinationList,
            };
          else
            return {
              ...ele,
              destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
                ...chatDataEle,
                checkedFlag: false,
              })),
            };
        })
      );

      /**
       * MOBILE or Desktop
       */
      if (isMobile) {
        navigateWithParams(ROUTES.ItineraryDetails);
      } else {
        setScreen("singleItinerary");
      }
    }
    setOpenLoaderOverlay(false);
    setCallSaveHistoryAPIFlag({
      flag: true,
      method: "PUT",
    });
  }

  useEffect(() => {
    if (Array.isArray(chatData)) {
      const ifSelected = chatData?.reduce((acc: number, eleChatData: iChatData) => {
        const destinationList = eleChatData?.destinationList;
        if (Array.isArray(destinationList)) {
          const selectedCount = destinationList?.reduce((subEle: number, eleDestList: iCityDetailsResult) => {
            return eleDestList?.checkedFlag ? subEle + 1 : subEle;
          }, 0);
          return acc + selectedCount;
        }
        return acc;
      }, 0);

      if (ifSelected === 0) {
        abortPlanTripDetails?.abort?.();
      }
    }
  }, [chatData]);

  /**
   * Handling sending message
   */
  // function extractStatement(str: string) {
  //   const match = str.match(/"statement":\s*"([^"]*)"/); // Match statement and handle newlines manually
  //   return match ? match[1].replace(/\\n/g, "\n") : null; // Replace escaped newlines with actual newlines
  // }

  function handleTryAgain() {
    toast.dismiss();
    // Analytics — retry-persistence signal. messageIndex points at the user
    // turn being retried (chatData.length - 1 is the failed assistant bubble
    // about to be replaced; -2 is the user message that needs retrying).
    const messageIndex = chatData.length >= 2 ? chatData.length - 2 : -1;
    triggerGAEvent(GA_LABEL_ENUMS.home_chat_try_again_click, "", "", "", { messageIndex });
    fireTheresiasEvent("home-chat-try-again-click", { messageIndex });
    setSignInClick(true);
    const rateLimitExceed = checkIfRateLimitExceed(userInfo?.loggedIn ?? false, appConfig);
    if (rateLimitExceed) {
      setLoginCreateAccountState({
        page: DialogSelected.LOGIN,
        flag: true,
        loginRequired: true,
      });
      return;
    }
    scrollToBottom(desktopScrollContainerRef);
    if (chatData.length > 0 && chatData[chatData.length - 1].isLoading) {
      toastMessage.info("Please wait, we are still fetching the result!");
      return;
    }
    // if (chatInputText === "") {
    //   toastMessage.info("Please type your message!");
    //   return;
    // }
    const retryEntryId = v4();
    setChatData((prev: iChatData[]) => [
      ...prev.slice(0, -1),
      {
        id: retryEntryId,
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
        isCompleteMessage: false,
      },
    ]);
    setChatInputText("");
    setDisableInput(true);
    chatAbortControllerRef.current = chatAbortController;

    const replayPayload = lastChatPayloadRef?.current;
    if (replayPayload && replayPayload.chatId === getChatId()) {
      outboundV2RequestRef.current = {
        messages: Array.isArray(replayPayload?.messages) ? replayPayload.messages : [],
        reqId: v4(),
        traceId: retryEntryId.slice(0, 8),
        pendingEntryId: retryEntryId,
        caller: "retry",
      };
      handleOpenSocketConnection(replayPayload);
      startSocketStreamingTimer();
      setRegeneratingItinerary(false);
      return;
    }

    // Build the conversation history directly from visible chat bubbles. The
    // previous pattern (find the most recent bubble whose response handler
    // happened to snapshot `prompt[]`) silently dropped every turn after the
    // last snapshot — so follow-up exchanges never reached the LLM.
    let promptContext: any = buildPromptContext(chatData);
    // Retry contract mirrors handleSendMessageStreaming: backend reassembles
    // `messages + [{role:"user", content:userInput}]`. If we sent userInput=""
    // here, the trailing real user turn already in `messages` would be followed
    // by a spurious `{role:"user", content:""}` in the LLM's view of history.
    // Pop the last user turn (the one we're retrying) off `messages` and pass
    // it as `userInput` so the assembled shape stays identical to a fresh send.
    let retryUserInput = "";
    if (
      promptContext.length > 0 &&
      promptContext[promptContext.length - 1]?.role === "user"
    ) {
      retryUserInput = promptContext[promptContext.length - 1]?.content || "";
      promptContext = promptContext.slice(0, -1);
    }
    const retryMessages = filterErrorMessages(promptContext);
    const payload: iChatStreamingPayload = {
      userId: userInfo?.id,
      chatId: getChatId(),
      userInput: retryUserInput,
      messages: retryMessages,
      socketId: socketId || "",
      iteneryInput: null,
      showMore: false,
      userLocation,
      // Backend uses accessToken to resolve the signed-in user for the LLM
      // turn (chat-search-v1 / V2 orchestrator). Keep in lockstep with the
      // pocContext send paths that already carry this field — without it the
      // retry request lands on the anonymous code path and degrades behavior.
      accessToken: accessJWT,
    };
    outboundV2RequestRef.current = {
      messages: retryUserInput ? [...retryMessages, { role: "user", content: retryUserInput }] : retryMessages,
      reqId: v4(),
      traceId: retryEntryId.slice(0, 8),
      pendingEntryId: retryEntryId,
      caller: "retry",
    };
    handleOpenSocketConnection(payload);
    startSocketStreamingTimer();
    setRegeneratingItinerary(false);
  }

  async function handleSendMessageStreaming() {
    // NOTE: `home-chat-send` Theresias event + `chat_input_field_send_prompt_to_llm_click`
    // GA event are fired from `pocContext.handleTopDestinationSendMessage` /
    // `handleNewTripSendMessage` — the ACTIVE path used by the current UI.
    // This function is the legacy alternative-streaming path and is
    // unreachable from the live chat surfaces, so the wires are intentionally
    // not duplicated here (would double-fire if this path ever becomes live
    // again without removing them).

    setChatData((prev: iChatData[]) => prev.filter((ele: iChatData) => !ele?.isProcessInterrupted));
    toast.dismiss();
    setSignInClick(true);
    const rateLimitExceed = checkIfRateLimitExceed(userInfo?.loggedIn ?? false, appConfig);
    if (rateLimitExceed) {
      setLoginCreateAccountState({
        page: DialogSelected.LOGIN,
        flag: true,
        loginRequired: true,
      });
      return;
    }
    scrollToBottom(desktopScrollContainerRef);
    if (chatData.length > 0 && chatData[chatData.length - 1].isLoading) {
      toastMessage.info("Please wait, we are still fetching the result!");
      return;
    }
    if (chatInputText === "") {
      toastMessage.info("Please type your message!");
      return;
    }
    setChatData((prev: iChatData[]) => [
      ...prev,
      {
        id: v4(),
        message: chatInputText,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
      },
      {
        id: v4(),
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
        isCompleteMessage: false,
      },
    ]);
    setChatInputText("");
    setDisableInput(true);
    chatAbortControllerRef.current = chatAbortController;

    // Shared-trip clone-on-first-chat: if selectedChatData was hydrated from
    // /shared/:id, ViewShareItinerary stamped `sharedSourceTravelPlanId` on
    // it; the helper either (a) recognizes the viewer as the source owner
    // and just stamps travelPlanId = sourceId (alreadyOwned, no save), or
    // (b) clones the source into the viewer's account, refreshes the chat
    // session id, and stamps travelPlanId = newId. Non-fatal on failure —
    // falls through to send anyway.
    const cloneResult = await cloneSharedTravelPlanIfNeeded({
      selectedChatData,
      accessJWT,
      userId: userInfo?.id,
    });
    const activeChatData: any = cloneResult.next;
    if (cloneResult.cloned || cloneResult.alreadyOwned) {
      setSelectedChatData(activeChatData);
      // Mirror travelPlanId + clear sharedSourceTravelPlanId on the synthetic
      // greeting bubble in chatData. Without this, any flow that re-derives
      // selectedChatData from a chatData entry resurrects the stale
      // sharedSourceTravelPlanId and the helper re-fires on the next send,
      // re-cloning under the viewer's account on every message.
      const stampedTravelPlanId = cloneResult.travelPlanId;
      const stampSavedFlag = cloneResult.alreadyOwned;
      setChatData((prev: iChatData[]) =>
        Array.isArray(prev)
          ? prev.map((entry: any) =>
              entry?.sharedSourceTravelPlanId
                ? {
                    ...entry,
                    travelPlanId: stampedTravelPlanId,
                    sharedSourceTravelPlanId: undefined,
                    ...(stampSavedFlag ? { isSaved: true } : {}),
                  }
                : entry
            )
          : prev
      );
    }

    // Build the conversation history directly from visible chat bubbles via
    // buildPromptContext (synthesizes destinations turns whose message is
    // empty, drops only the loading placeholder).
    let promptContext: any = buildPromptContext(chatData);
    // For shared-trip resume, chatData is just the synthetic greeting bubble
    // (or post-clone the LLM has no server-side memory under the new chat
    // session id). Always pass the hydrated itinerary via currentItineray on
    // the just-cloned / just-recognized turn so the system prompt's
    // {{CURRENT_ITINERARY}} slot is filled — buildPromptContext synthesizes
    // a "Showed destination cards: ..." line from the greeting bubble's
    // destinationList which is non-empty but useless as trip context, so
    // the noPromptContext branch alone isn't enough.
    const noPromptContext = !promptContext || promptContext.length === 0;
    const hasItinerary =
      Array.isArray(activeChatData?.destinationList) && activeChatData.destinationList.length > 0;
    const justClonedOrOwned = cloneResult.cloned || cloneResult.alreadyOwned;
    // Send currentItineray when:
    //   - noPromptContext (fresh chat session), OR
    //   - just cloned / recognized as already-owned (LLM has no memory of
    //     the clone's chat session yet), OR
    //   - the trip is still in shared-source state (clone failed or hasn't
    //     run yet — without sending the itinerary the LLM has no idea
    //     which trip the viewer is refining), OR
    //   - the trip came from a shared-link clone (travelPlanId set). The
    //     clone gave it a fresh _zenvoya_chat_id with no server-side LLM
    //     memory; sending currentItineray on every send keeps the LLM
    //     anchored to the trip across the conversation.
    const stillInSharedContext = !!activeChatData?.sharedSourceTravelPlanId;
    const fromSharedClone = !!activeChatData?.travelPlanId;
    const currentItineray =
      (noPromptContext || justClonedOrOwned || stillInSharedContext || fromSharedClone) &&
      hasItinerary
        ? sanitizeItineraryForLLM(activeChatData)
        : undefined;
    const payload: iChatStreamingPayload = {
      userId: userInfo?.id,
      chatId: getChatId(),
      userInput: chatInputText,
      messages: filterErrorMessages(promptContext),
      socketId: socketId || "",
      iteneryInput: null,
      showMore: false,
      userLocation,
      ...(currentItineray ? { currentItineray } : {}),
    };
    // eslint-disable-next-line no-console
    console.log("[LLM-PAYLOAD] chat-send →", {
      chatId: payload.chatId,
      userInput: payload.userInput,
      messagesCount: payload.messages?.length ?? 0,
      messages: payload.messages,
      currentItineraryIncluded: !!currentItineray,
      currentItineraryDestinations: currentItineray?.destinationList?.map((d: any) => d?.city_name || d?.destination_name) ?? null,
      cloneResult: { cloned: cloneResult.cloned, alreadyOwned: cloneResult.alreadyOwned, travelPlanId: cloneResult.travelPlanId },
      activeChatDataTravelPlanId: activeChatData?.travelPlanId,
      activeChatDataSharedSourceFlag: activeChatData?.sharedSourceTravelPlanId,
      payload,
    });
    handleOpenSocketConnection(payload);
    startSocketStreamingTimer();
    setRegeneratingItinerary(false);
  }

  async function handleSendMessage() {
    toast.dismiss();
    setSignInClick(true);
    const rateLimitExceed = checkIfRateLimitExceed(userInfo?.loggedIn ?? false, appConfig);
    if (rateLimitExceed) {
      setLoginCreateAccountState({
        page: DialogSelected.LOGIN,
        flag: true,
      });
      return;
    }
    scrollToBottom(desktopScrollContainerRef);
    if (chatData.length > 0 && chatData[chatData.length - 1].isLoading) {
      toastMessage.info("Please wait, we are still fetching the result!");
      return;
    }
    if (chatInputText === "") {
      toastMessage.info("Please type your message!");
      return;
    }
    setChatData((prev: iChatData[]) => [
      ...prev,
      {
        id: v4(),
        message: chatInputText,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
      },
      {
        id: v4(),
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
      },
    ]);
    setChatInputText("");
    setDisableInput(true);
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Build the conversation history directly from visible chat bubbles (see
    // comment in handleSendMessageStreaming above). The previous reverse+find
    // pattern dropped follow-up exchanges that didn't snapshot prompt[].
    const promptContext: any = buildPromptContext(chatData);
    let response = await chatInteractionAPI(
      accessJWT,
      userInfo?.id || "",
      chatInputText,
      promptContext,
      abortController.signal,
      null,
      appConfig
    );

    if (abortController.signal.aborted) {
      setChatData((prev: iChatData[]) => {
        const length = prev.length;
        if (prev.length > chatData.length) return [...prev.slice(0, length - 2)];
        return prev;
      });
      return;
    }

    /**
     * Recalling the API if it fails
     */
    if (checkIfThereIsDestination(response) === LLM_RESPONSE_ERROR.jsonError.error) {
      response = await chatInteractionAPI(
        accessJWT,
        userInfo?.id || "",
        chatInputText + INVALID_JSON_PROMPT,
        promptContext,
        // chatData.length > 0 ? chatData[chatData.length - 1].prompt : [],
        abortController.signal,
        null,
        appConfig
      );
    }

    if (response === ERR_NETWORK) {
      setDisableInput(false);
      appendErrorMessageTochat(chatData, setChatData, response?.message ?? LLM_RESPONSE_ERROR.timeOut.message);
      return;
    }

    if (response === null || (!response && !abortPlanTripDetails.signal.aborted) || !response?.data?.result) {
      setDisableInput(false);
      appendErrorMessageTochat(chatData, setChatData, response?.message ?? LLM_RESPONSE_ERROR.jsonError.message);
      return;
    }
    response = checkIfThereIsDestination(response);
    if (response === LLM_RESPONSE_ERROR.jsonError.error) {
      setDisableInput(false);

      appendErrorMessageTochat(chatData, setChatData, response?.message ?? LLM_RESPONSE_ERROR.jsonError.message);
      if (environment !== ENVIRONMENT_TYPE.PRODUCTION) {
        toastMessage.error(
          Messages["SaveItinerary.destinationError"].messageTitle,
          Messages["SaveItinerary.destinationError"].message
        );
      }
      return;
    }
    let citiesList = [];
    if (response?.data?.cities) {
      citiesList = response?.data?.cities;
    }
    processedAndGetDestinationImageURL(citiesList, response, false);
    setDisableInput(false);
    scrollToBottom(desktopScrollContainerRef);
    setCallSaveHistoryAPIFlag({
      flag: true,
      method: "PUT",
    });
    updateRateLimitCount();
  }
  async function processedAndGetDestinationImageURL(
    citiesList: iCityDetailsResult[],
    response: any,
    showMore: boolean
  ) {
    if (citiesList.length > 0) {
      const tempResponse = citiesList.map((ele: any) => ({
        ...ele,
        id: v4(),
        checkedFlag: false,
        locations: [],
      }));
      setChatData((prev: iChatData[]) => [
        ...prev.slice(0, prev.length - 1),
        {
          results: response?.data?.results,
          ...response?.data,
          cities: [],
          city_wise_itinerary: [],
          result: "",
          messages: [],
          fromCity: response?.data?.from,
          noOfDays: response?.data?.numberOfdays,
          id: showMore ? prev[prev.length - 1].id : v4(),
          message: "",
          preText: response?.data?.preText,
          postText: response?.data?.postText,
          prompt: response?.data?.messages,
          isUserMessage: false,
          isLoading: false,
          singleSelectedDestination: response?.data?.singleCity,
          destinationList: showMore ? [...prev[prev.length - 1].destinationList, ...tempResponse] : tempResponse,
          itinerary_version: getItineraryVersion(prev.slice(0, prev.length - 1), response?.data?.results),
        },
      ]);
      const currentItineraryList = tempResponse;
      for (let i = 0; i < currentItineraryList.length; i++) {
        const cityName =
          currentItineraryList[i]?.destination_city ||
          currentItineraryList[i]?.city_name ||
          currentItineraryList[i]?.destination_city ||
          "";
        let imageRespone: any = await getItineraryImageURL(accessJWT, cityName);
        let count = 0;
        while (!imageRespone || !imageRespone?.data?.image) {
          // Limiting the number of API call to 10 times incase of 409 error
          count++;
          if (count === 3) break;
          // re calling the API
          imageRespone = await getItineraryImageURL(accessJWT, cityName);
        }
        if (imageRespone?.statusCode === 200) {
          currentItineraryList[i].imageURL = imageRespone.data.image;
        } else {
          currentItineraryList[i].imageURL = "https://error.error";
        }
      }
      if (ENABLE_GA_CUSTOM_EVENTS) {
        ReactGA.event({
          category: "Top Destination",
          action: `User has recieved the itinerary list created`,
          label: `User prompt is ${response?.data?.query}`,
        });
      }
    } else {
      setChatData((prev: iChatData[]) => [
        ...prev.slice(0, prev.length - 1),
        {
          id: v4(),
          message: response?.data?.result,
          prompt: response?.data?.messages,
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
      ]);
    }
  }

  /**
   * Handling cancelling Send message or
   * open Itinerary details or Show More
   */
  function handleCancelStreaming({ trackStop = true }: { trackStop?: boolean } = {}) {
    // Build the conversation history directly from visible chat bubbles. The
    // user's just-sent message (at chatData[length - 2]) is naturally
    // included; the assistant placeholder at length - 1 is excluded by the
    // !isLoading filter. The previous reverse+find pattern was dropping
    // mid-conversation follow-up exchanges.
    const promptContext: any = buildPromptContext(chatData);
    consoleLog(["PROMPT CONTEXT", promptContext]);
    // Analytics — abandonment signal. partialTokensSeen is not currently
    // tracked in streaming state; -1 sentinel until a token counter is wired
    // alongside the streaming response handler.
    const stopCustomObj = { messagesAtStop: chatData.length, partialTokensSeen: -1 };
    if (trackStop) {
      triggerGAEvent(
        GA_LABEL_ENUMS.chat_input_field_cancel_generating_llm_response,
        "",
        "",
        "",
        stopCustomObj
      );
      fireTheresiasEvent("home-chat-input-stop-click", stopCustomObj);
    }

    handleCloseSocketConnection();
    if (isMobile) navigateWithParams(ROUTES.TopDestination);
    else {
      setScreen("");
      setFirstScreen((prev: any) => ScreenTypes.topDestination);
    }
    setShowChatLoaderFlag(false);
    setStreamingFlagAndResponseType({
      isStillStreamingFlag: false,
      chatId: "",
      responseType: "",
      showMoreClickFlag: false,
    });
    setDisableInput(false);
    setIsItinerarySavedFlag(false);
    setSharedItineraryId("");
    setShowChatLoaderFlag(false);
    // Always clear the loading bubble before any early return. Covers the case
    // where API Gateway rejects the payload (>100 KB) before the backend's
    // "connected" frame is sent — isStillStreamingFlag stayed false, so the
    // early return below would otherwise fire and leave isLoading=true on the
    // placeholder. The branches further down replace this bubble entirely for
    // mid-streaming cancellations; here we just guarantee the loader can't get
    // stuck visually out-of-sync with disableInput=false.
    setChatData((prev: iChatData[]) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (!last?.isLoading) return prev;
      return [
        ...prev.slice(0, -1),
        { ...last, isLoading: false, isProcessInterrupted: true },
      ];
    });
    if (!streamingFlagAndResponseType?.isStillStreamingFlag) return;

    if (streamingFlagAndResponseType?.responseType === "destinations" && chatData?.length === 2) {
      setChatData((prev: iChatData[]) => [
        ...prev.slice(0, -1),
        {
          id: v4(),
          message: "",
          prompt: promptContext,
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
          isProcessInterrupted: true,
        },
      ]);
      if (isMobile) navigateWithParams(ROUTES.NewTrip);
      else {
        setScreen("");
        setFirstScreen((prev: any) => ScreenTypes.NewTrip);
      }
    } else if (
      streamingFlagAndResponseType?.chatId === "" &&
      streamingFlagAndResponseType?.showMoreClickFlag === false
    ) {
      setChatData((prev: any) => [
        ...prev.slice(0, -1),
        {
          id: v4(),
          message: "",
          prompt: promptContext,
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
          isProcessInterrupted: true,
        },
      ]);
    } else if (streamingFlagAndResponseType?.showMoreClickFlag === true) {
      setChatData((prev: any) =>
        prev.map((ele: iChatData) => ({
          ...ele,
          destinationList: ele?.destinationList?.filter(
            (eleDestList: iCityDetailsResult) => eleDestList?.destination_name
          ),
        }))
      );
    } else {
      // Follow-up message cancellation (chatId is set, not show-more). The
      // three branches above never match this case, so the loading assistant
      // bubble would otherwise keep isLoading: true forever — visibly stuck
      // when the WS dies silently (e.g., API Gateway closes on >100 KB
      // payload without firing END/ERROR). Replace the bubble with an
      // interrupted placeholder, same shape as the initial-chat branch above,
      // so the user gets the Try Again UI.
      setChatData((prev: any) => [
        ...prev.slice(0, -1),
        {
          id: v4(),
          message: "",
          prompt: promptContext,
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
          isProcessInterrupted: true,
        },
      ]);
    }
  }

  // Auto-cancel when the WebSocket dies with a request in flight. API Gateway
  // closes the socket on oversized payloads (>100 KB) or transient network
  // issues without firing END/ERROR, leaving the loading bubble stuck. Next
  // send is then blocked by the "Please wait, we are still fetching the
  // result!" guard, so the user can't recover without a manual cancel.
  //
  // Gate on `disableInput` (set true at send, cleared on
  // completion/cancel/error) OR `isStillStreamingFlag`. The flag alone isn't
  // enough — it only flips true when the backend's first frame arrives
  // (DataStreamingContext "connected" handler). If API Gateway rejects the
  // payload before any frame is received, the flag stays false but
  // `disableInput` is true. `disableInput` covers that pre-streaming window;
  // `isStillStreamingFlag` covers any path that resets disableInput first.
  //
  // CRITICAL: only fire on the TRANSITION into closed (non-closed → closed),
  // not whenever the effect re-evaluates with state still closed. The effect
  // also re-runs when `disableInput` changes, so if a previous turn left the
  // socket closed and the user hits Send on a new message, `disableInput`
  // flips true and the effect runs again — without the transition guard we'd
  // cancel the brand-new request before it can even connect (visible in the
  // logs as `ws.connecting` → `ws.close {manualClose:true}` with no `ws.open`
  // between them).
  //
  // Ref-indirection so we always call the latest handleCancelStreaming (it
  // reads current chatData), without re-running the effect every render.
  const cancelStreamingRef = useRef<() => void>(() => {});
  cancelStreamingRef.current = handleCancelStreaming;
  const prevConnectionStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevConnectionStatusRef.current;
    prevConnectionStatusRef.current = connectionStatus;
    if (
      prev !== SOCKET_CONNECTION_TYPE.closed &&
      connectionStatus === SOCKET_CONNECTION_TYPE.closed &&
      (disableInput || streamingFlagAndResponseType?.isStillStreamingFlag)
    ) {
      cancelStreamingRef.current();
    }
  }, [connectionStatus, disableInput, streamingFlagAndResponseType?.isStillStreamingFlag]);

  function handleCancel() {
    setShowChatLoaderFlag(false);
    setScreen("");
    abortPlanTripDetails.abort();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setDisableInput(false);
    }
    if (chatAbortControllerRef.current) {
      chatAbortControllerRef.current.abort();
      setDisableInput(false);
    }
    setIsItinerarySavedFlag(false);
    setSharedItineraryId("");
    setShowChatLoaderFlag(false);
    if (!streamingFlagAndResponseType?.isStillStreamingFlag) return;

    if (chatData[chatData.length - 1].prompt.length === 0) {
      const tempChatData = chatData;
      setChatData(tempChatData.slice(0, -2));
    } else {
      setChatData(
        chatData.map((eleChatData: iChatData) => ({
          ...eleChatData,
          isLoading: false,
        }))
      );
    }
    // setSelectedChatData()
    let tempDestinationList: iCityDetailsResult[] =
      chatData.find((ele: iChatData) => ele.id === selectedCheckboxDestinationId.destinationArgsId)?.destinationList ||
      ([] as iCityDetailsResult[]);
    tempDestinationList = tempDestinationList.map((ele: iCityDetailsResult) => {
      if (selectedCheckboxDestinationId.cityDetailsId === ele.id) return { ...ele, checkedFlag: !ele.checkedFlag };
      return { ...ele, checkedFlag: false };
    });
    setChatData((prev: iChatData[]) =>
      prev.map((ele: iChatData) => {
        if (ele.id === selectedCheckboxDestinationId.destinationArgsId)
          return {
            ...ele,
            destinationList: tempDestinationList,
          };
        else
          return {
            ...ele,
            destinationList: ele.destinationList.map((chatDataEle: iCityDetailsResult) => ({
              ...chatDataEle,
              checkedFlag: false,
            })),
          };
      })
    );
  }

  const [openFeedbackDialogFlag, setOpenFeedbackDialogFlag] = useState(false);
  const [openFeedbackDrawerActionFlag, setOpenFeedbackDrawerActionFlag] = useState(false);

  useEffect(() => {
    state.setIsBottomDrawerOpen(openFeedbackDrawerActionFlag);
    return () => state.setIsBottomDrawerOpen(false);
  }, [openFeedbackDrawerActionFlag]);

  /**
   * Value of the Context
   */
  const value = useMemo(
    () => ({
      openFeedbackDialogFlag,
      setOpenFeedbackDialogFlag,
      openFeedbackDrawerActionFlag,
      setOpenFeedbackDrawerActionFlag,
      handleCheckboxChange: STREAMING_ENABLE ? handleCheckboxChangeStreaming : handleCheckboxChange,
      handleShowMore: STREAMING_ENABLE ? handleShowMoreStreaming : handleShowMore,
      handleViewSingleTripDetails,
      showChatLoaderFlag,
      navigate,
      disableInput,
      handleSendMessage: STREAMING_ENABLE ? handleSendMessageStreaming : handleSendMessage,
      handleCancel: STREAMING_ENABLE ? handleCancelStreaming : handleCancel,
      // desktopScrollContainerRef,
      openLoaderOverlay,
      handleTryAgain,
    }),
    [
      openFeedbackDialogFlag,
      setOpenFeedbackDialogFlag,
      openFeedbackDrawerActionFlag,
      setOpenFeedbackDrawerActionFlag,
      handleCheckboxChangeStreaming,
      handleCheckboxChange,
      handleShowMoreStreaming,
      handleShowMore,
      handleViewSingleTripDetails,
      showChatLoaderFlag,
      navigate,
      disableInput,
      handleSendMessageStreaming,
      handleSendMessage,
      handleCancelStreaming,
      handleCancel,
      openLoaderOverlay,
      handleTryAgain,
    ]
  );
  return (
    <TopDestinationContext.Provider value={value}>
      <SwipeableEdgeDrawer
        openFeedbackDrawerActionFlag={openFeedbackDrawerActionFlag}
        setOpenFeedbackDrawerActionFlag={setOpenFeedbackDrawerActionFlag}
        onNeedImprovementClick={() => setOpenFeedbackDialogFlag(true)}
      />
      <UserFeedbackDialog
        open={openFeedbackDialogFlag}
        onClose={() => {
          setOpenFeedbackDialogFlag(false);
        }}
      />
      {children}
    </TopDestinationContext.Provider>
  );
};

export const useTopDestinationContext = () => useContext(TopDestinationContext);
